import React, { useEffect, useState } from "react";
import { Button, Col, Container, Dropdown, Form, Row } from "react-bootstrap";
import { CommonToolTip, CstmPagination } from "components/Common/Common";
import { useNavigate } from "react-router-dom";
import LoadingIndicator from "components/shared/LoadingIndicator";
import LoadingOverlay from "components/shared/LoadingOverlay";
import { ArchiveIcon, DeleteIcon, EditIcon, UnArchiveIcon } from "Assets/icons";
import TableLayout from "components/TableLayout";
import ErrorComponent from "components/shared/ErrorComponent";
import TableHeader from "components/shared/FormComponents/TableHeader";
import { useDispatch, useSelector } from "react-redux";
import { exportFilter } from "store/slices/filter";
import { useGetExportListQuery, useRegenerateExportMutation, useRegenerateExportQuery } from "store/services/export";
import { EXPORT_MODULES, EXPORT_STATUS, EXPORT_STATUS_OPTIONS } from "config/constants";
import moment from "moment";
import ConfirmationPop from "components/Modals/ConfirmationPop";
import { successMessageToast } from "helpers/common";

const ManageExport = () => {

    const dispatch = useDispatch();
    const filter = useSelector(state => state?.filter?.exportFilter);

    const [showModal, setShowModal] = useState(false);
    const [currentEntry, setCurrentEntry] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [exportList, setExportList] = useState([]);

    const { data, isLoading, isError, isFetching, refetch } = useGetExportListQuery({ page: currentPage, limit: pageSize, ...filter });
    const totalRecords = data?.totalRecords || 0;
    const totalPages = Math.ceil(totalRecords / pageSize);

    const [regenerate, { isLoading: regenerateLoading }] = useRegenerateExportMutation();

    const column = [
        {
            head: "Module",
            accessor: "module",
            isComponent: true,
            sorter: true,
            component: (item, ind) => (
                <h6 className="m-0 fw-bold themeBlue">{EXPORT_MODULES[item?.module_name]}</h6>
            ),
        },
        {
            head: "Date / Time",
            accessor: "module",
            isComponent: true,
            component: (item, ind) => (
                <h6 className="m-0 fw-bold themeBlue">{moment(item?.createdAt).format("DD/MM/YYYY  HH:mm A")}</h6>
            ),
        },
        {
            head: "Status",
            accessor: "status",
            sorter: true,
            isComponent: true,
            component: (item, ind) => (
                <p
                    style={{ width: 80 }}
                    className={`${item.status == "C"
                        ? "greenStatus"
                        : item.status == "I" ? "yellowStatus" : "redStatus"
                        } status border fw-bold text-capitalize m-0 text-center d-flex align-items-center justify-content-center rounded-pill px-4 py-1`}
                >
                    {EXPORT_STATUS[item?.status]}
                </p>
            ),
        },
        {
            head: "Action",
            accessor: "",
            isComponent: true,
            component: (item) => (
                <div className="ActnBtn d-flex align-items-center gap-10">

                    {["P"].includes(item?.status) && <CommonToolTip
                        text={"In Progress"}
                        component={
                            <div style={{ height: "20px", width: "20px", marginLeft: "4px" }} class="export-spinner spinner-border" role="status">
                                <span class="sr-only">Loading...</span>
                            </div>
                        }
                    />}

                    {["C", "E", "I"].includes(item?.status) && <CommonToolTip
                        text={"Regenerate"}
                        component={<Button
                            className="p-0"
                            variant="transparent"
                            type="button"
                            onClick={() => {

                                setCurrentEntry(item);
                                setShowModal(true);
                            }}
                        >
                            <svg style={{ color: "#b46c9f" }} xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" class="bi bi-arrow-counterclockwise" viewBox="0 0 14 14">
                                <path fill-rule="evenodd" d="M8 3a5 5 0 1 1-4.546 2.914.5.5 0 0 0-.908-.417A6 6 0 1 0 8 2z" />
                                <path d="M8 4.466V.534a.25.25 0 0 0-.41-.192L5.23 2.308a.25.25 0 0 0 0 .384l2.36 1.966A.25.25 0 0 0 8 4.466" />
                            </svg>
                        </Button>}
                    />}

                    {(["C", "E"].includes(item?.status) && item?.export_file_key) && <CommonToolTip
                        text={"Download"}
                        component={<Button
                            className="p-0 mx-2"
                            variant="transparent"
                            type="button"
                            onClick={() => {
                                window.open(item?.export_file_key, '_blank');
                            }}
                        >
                            <svg style={{ color: "#b46c9f" }} xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-download" viewBox="0 0 16 16">
                                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                            </svg>
                        </Button>}
                    />}

                </div>
            ),
        },
    ]

    const handleFilterChange = (key) => (value) => {
        dispatch(exportFilter({ ...filter, [key]: value }))
        setCurrentPage(1);
    }

    const handlePageChange = (page) => {
        window.scrollTo(0, 0);
        setCurrentPage(page);
    };

    const resetFilter = () => {
        dispatch(exportFilter({ status: "", sort: { column: "id", direction: "desc" } }));
        setCurrentPage(1)
    };

    const handleRegenerate = async () => {
        try {
            setShowModal(false);

            const list = [...exportList];
            const index = exportList?.findIndex(x => x.id === currentEntry?.id);
            list[index] = { ...list[index], status: "P" };
            setExportList(list.filter(Boolean));

            const result = await regenerate(currentEntry?.id);
            if (result.error) return;

            successMessageToast(result?.data?.message);
        } catch (error) { console.log("Error regenerating export") };
    };

    useEffect(() => {

        if (data?.records) setExportList(data?.records)
    }, [data]);

    return (
        <>
            <ConfirmationPop
                confirmation={showModal}
                setConfirmation={setShowModal}
                type={"regenerate"}
                confirmHandler={handleRegenerate}
            />
            <section className="ManageDiscriplines position-relative py-4">
                <Container fluid={true}>
                    <Row>
                        <Col lg="12" className="my-2">
                            <div className="TableFilter d-flex align-items-center justify-content-between gap-10 flex-wrap">
                                <div className="left">
                                    <TableHeader
                                        heading={"Export"}
                                        handleFilterChange={handleFilterChange}
                                        resetFilter={resetFilter}
                                        filter={filter}
                                        hideSearch
                                        hideStatusFilter
                                        filterComponent={
                                            <>
                                                <li className="">
                                                    <Dropdown
                                                        onSelect={handleFilterChange("status")}
                                                    >
                                                        <Dropdown.Toggle
                                                            variant="transparent"
                                                            className="border-0 d-flex align-items-center  justify-content-center"
                                                            id="dropdown-basic"
                                                            style={{ minWidth: "100px" }}
                                                        >
                                                            {filter.status ? EXPORT_STATUS_OPTIONS?.find(x => x.value == filter.status)?.label || "Status" : "Status"}
                                                            <span className="icn ms-2">
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="12"
                                                                    height="8"
                                                                    viewBox="0 0 12 8"
                                                                    fill="none"
                                                                >
                                                                    <path
                                                                        d="M1.415 0.298828L6 4.731L10.585 0.298828L12 1.66666L6 7.46666L0 1.66666L1.415 0.298828Z"
                                                                        fill="black"
                                                                    />
                                                                </svg>
                                                            </span>
                                                        </Dropdown.Toggle>

                                                        <Dropdown.Menu
                                                            style={{
                                                                maxHeight: "200px",
                                                                overflowY: "scroll"
                                                            }}
                                                        >
                                                            {
                                                                EXPORT_STATUS_OPTIONS?.map((item, index) => {
                                                                    return <Dropdown.Item key={index} eventKey={item.value}>{item?.label}</Dropdown.Item>
                                                                })
                                                            }
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                </li>
                                            </>
                                        }
                                    />
                                </div>
                            </div>
                            <div className="table-outer">
                                {isLoading ? (
                                    <div style={{ height: "200px" }}>
                                        <LoadingIndicator />
                                    </div>
                                ) : isError ? (
                                    <ErrorComponent />
                                ) : (
                                    <>
                                        {(isFetching || regenerateLoading) && (
                                            <LoadingOverlay />
                                        )}
                                        <TableLayout data={exportList} column={column} filter={filter} handleSort={handleFilterChange("sort")} />
                                    </>
                                )}
                                <CstmPagination
                                    totalPages={totalPages}
                                    activePage={currentPage}
                                    handlePageChange={handlePageChange}
                                />
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </>
    );
};

export default ManageExport;
